import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { api } from '../../lib/api'
import { useOsCreate, type OrdemServicoCreateInput } from '../../hooks/useOs'
import { useEquipamentoList } from '../../hooks/useEquipamentos'
import { useUsuarioList } from '../../hooks/useUsuarios'
import type { PaginatedResponse } from '../../types'
import styles from '../_shared/CrudPage.module.css'
import formStyles from '../_shared/CrudForm.module.css'

const TIPOS_OS = [
  { value: 'preventivo', label: 'Preventivo' },
  { value: 'corretivo', label: 'Corretivo' },
  { value: 'higienizacao', label: 'Higienização' },
  { value: 'qai', label: 'Análise de QAI' },
  { value: 'desinfeccao_legionella', label: 'Desinfecção — Legionella' },
  { value: 'emergencial', label: 'Emergencial' },
]

const PRIORIDADES = [
  { value: 'normal', label: 'Normal' },
  { value: 'alta', label: 'Alta' },
  { value: 'urgente', label: 'Urgente' },
  { value: 'emergencial', label: 'Emergencial' },
]

interface PmocResumo {
  id: string
  titulo: string
  // Pós-#67: cliente_nome substitui site_nome. Mantemos os legados como
  // opcionais para a UI funcionar tanto em master pré-merge quanto pós.
  cliente_nome?: string
  cliente?: string
  site_nome?: string
  site?: string
}

function extractErrorMsg(err: unknown): string {
  const e = err as { response?: { data?: Record<string, unknown> } }
  const data = e?.response?.data ?? {}
  for (const k of ['detail', 'pmoc', 'equipamento', 'tipo_os', 'data_prevista', 'titulo']) {
    const v = data[k]
    if (Array.isArray(v) && v.length) return `${k}: ${String(v[0])}`
    if (typeof v === 'string') return `${k}: ${v}`
  }
  return 'Erro ao criar OS — verifique os campos.'
}

export default function OrdemServicoNovaPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const pmocPreSelecionado = params.get('pmoc') ?? ''
  const create = useOsCreate()

  const [form, setForm] = useState<OrdemServicoCreateInput>({
    pmoc: pmocPreSelecionado,
    tipo_os: 'preventivo',
    prioridade: 'normal',
    data_prevista: new Date().toISOString().slice(0, 10),
    equipamento: null,
    tecnico_responsavel: null,
    titulo: '',
    descricao: '',
  })

  const pmocs = useQuery({
    queryKey: ['pmoc-list-resumo'],
    queryFn: async () => {
      const r = await api.get<PaginatedResponse<PmocResumo>>('/pmoc/', {
        params: { page_size: 200 },
      })
      return r.data.results
    },
  })

  const equipamentos = useEquipamentoList({})
  const usuarios = useUsuarioList()

  // Quando seleciona PMOC, gera título sugerido se vazio
  useEffect(() => {
    if (form.pmoc && !form.titulo) {
      const pmoc = pmocs.data?.find((p) => p.id === form.pmoc)
      if (pmoc) setForm((f) => ({ ...f, titulo: `OS ${form.tipo_os} — ${pmoc.titulo}` }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.pmoc, form.tipo_os])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.pmoc) {
      notifications.show({ color: 'red', message: 'Selecione o PMOC.' })
      return
    }
    if (!form.data_prevista) {
      notifications.show({ color: 'red', message: 'Informe a data prevista.' })
      return
    }
    try {
      const os = await create.mutateAsync(form)
      notifications.show({ color: 'green', message: `OS ${os.numero ?? ''} criada.` })
      navigate(`/ordens-servico/${os.id}`)
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Falha ao criar OS',
        message: extractErrorMsg(err),
      })
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <button
            className={styles.backBtn}
            onClick={() => navigate('/ordens-servico')}
            type="button"
          >
            ← Voltar
          </button>
          <h1 className={styles.title}>Nova Ordem de Serviço</h1>
        </div>
      </header>

      <form onSubmit={handleSubmit} className={formStyles.form}>
        <section className={formStyles.section}>
          <h2 className={formStyles.sectionTitle}>Dados</h2>
          <div className={formStyles.grid2}>
            <div className={formStyles.field}>
              <label htmlFor="pmoc">PMOC *</label>
              <select
                id="pmoc"
                data-testid="os-pmoc"
                value={form.pmoc}
                onChange={(e) => setForm({ ...form, pmoc: e.target.value })}
                disabled={pmocs.isLoading}
                required
              >
                <option value="">{pmocs.isLoading ? 'Carregando...' : '— selecione —'}</option>
                {pmocs.data?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.titulo}
                  </option>
                ))}
              </select>
            </div>

            <div className={formStyles.field}>
              <label htmlFor="tipo_os">Tipo de OS *</label>
              <select
                id="tipo_os"
                data-testid="os-tipo"
                value={form.tipo_os}
                onChange={(e) => setForm({ ...form, tipo_os: e.target.value })}
                required
              >
                {TIPOS_OS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            <div className={formStyles.field}>
              <label htmlFor="prioridade">Prioridade</label>
              <select
                id="prioridade"
                data-testid="os-prioridade"
                value={form.prioridade}
                onChange={(e) =>
                  setForm({
                    ...form,
                    prioridade: e.target.value as OrdemServicoCreateInput['prioridade'],
                  })
                }
              >
                {PRIORIDADES.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>

            <div className={formStyles.field}>
              <label htmlFor="data_prevista">Data prevista *</label>
              <input
                id="data_prevista"
                data-testid="os-data-prevista"
                type="date"
                value={form.data_prevista}
                onChange={(e) => setForm({ ...form, data_prevista: e.target.value })}
                required
              />
            </div>

            <div className={formStyles.field}>
              <label htmlFor="equipamento">Equipamento (opcional)</label>
              <select
                id="equipamento"
                data-testid="os-equipamento"
                value={form.equipamento ?? ''}
                onChange={(e) =>
                  setForm({ ...form, equipamento: e.target.value || null })
                }
                disabled={equipamentos.isLoading}
              >
                <option value="">— todos os equipamentos do PMOC —</option>
                {equipamentos.data?.results.map((eq) => (
                  <option key={eq.id} value={eq.id}>
                    {eq.marca} {eq.modelo} ({eq.tipo.replace(/_/g, ' ')})
                  </option>
                ))}
              </select>
            </div>

            <div className={formStyles.field}>
              <label htmlFor="tecnico">Técnico (opcional)</label>
              <select
                id="tecnico"
                data-testid="os-tecnico"
                value={form.tecnico_responsavel ?? ''}
                onChange={(e) =>
                  setForm({ ...form, tecnico_responsavel: e.target.value || null })
                }
                disabled={usuarios.isLoading}
              >
                <option value="">— atribuir depois —</option>
                {usuarios.data?.results
                  .filter((u) => u.perfil === 'executora')
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.nome_completo} ({u.email})
                    </option>
                  ))}
              </select>
            </div>

            <div className={`${formStyles.field} ${formStyles.span2}`}>
              <label htmlFor="titulo">Título</label>
              <input
                id="titulo"
                data-testid="os-titulo"
                value={form.titulo ?? ''}
                onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                placeholder="(gera fallback automático se vazio)"
              />
            </div>

            <div className={`${formStyles.field} ${formStyles.span2}`}>
              <label htmlFor="descricao">Descrição</label>
              <textarea
                id="descricao"
                data-testid="os-descricao"
                value={form.descricao ?? ''}
                onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                rows={4}
              />
            </div>
          </div>
        </section>

        <div className={formStyles.actions}>
          <button
            type="submit"
            className={formStyles.btnPrimary}
            disabled={create.isPending}
            data-testid="os-criar-submit"
          >
            {create.isPending ? 'Criando...' : 'Criar OS'}
          </button>
        </div>
      </form>
    </div>
  )
}
